import styled from "@emotion/styled";
import Modal from "@mui/material/Modal";
import * as React from "react";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { customApiCall } from "../utils/axios";
import { Button } from "./ButtonNew";
import { Dropzone } from "./Dropzone";
import { Icon } from "./Icon";

export default function CustomizedDialogs({
  Component,
  fileMetaDataAPI = undefined,
  formDataFileKeyName,
  headerText: userHeaderText = "",
  headerTitle: userHeaderTitle = "",
  onSuccess,
  onUploadDone = undefined,
  queryParams,
  renderModalBody = undefined,
  successMessage: userSuccessMessage = "",
  url,
}) {
  const { t } = useTranslation();

  let queryParamsList = [];

  const successMessage = userSuccessMessage || t("general.successfulImgaeUpload");
  const headerText = userHeaderText || t("general.dragToUpload");
  const headerTitle = userHeaderTitle || t("general.upload");

  const [errorMessage, setErrorMessage] = useState("");
  const [hasError, setHasError] = useState(false);
  const { user } = useSelector((store) => store.user);
  const token = user?.access_token;

  const handleSuccess = (resp) => {
    setHasError(false);
    setErrorMessage("");
    onSuccess?.(resp);
  };
  const handleError = (xhr) => {
    setHasError(true);
    setErrorMessage(xhr?.statusText || "");
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [uploadState, setUploadState] = useState("FileNotSelected");
  const [selectedFileName, setSelectedFileName] = useState("FileNotSelected");
  const [progress, setProgress] = useState(0);
  const [xhr, setXHR] = useState(new XMLHttpRequest());

  const { acceptedFiles, getInputProps, getRootProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
  });

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      setUploadState("FileSelected");
      setSelectedFileName(acceptedFiles.length === 0 ? "" : acceptedFiles[0].path);
    }
  }, [acceptedFiles]);

  var percent = 0;

  const uploadXHR = async () => {
    setUploadState("UploadingFileMetaData");
    var formData = new FormData();
    let fileMetaData = null;

    const fileToUpload = acceptedFiles[0];

    if (fileMetaDataAPI?.url) {
      try {
        fileMetaData = await customApiCall.post({
          data: fileMetaDataAPI?.getFileMetaDataBody?.(fileToUpload) ?? {},
          url: fileMetaDataAPI?.url,
        });
      } catch (error) {
        setUploadState("UploadFinished");
        const errorMessage = JSON.stringify(error.response.data);
        toast.error(errorMessage);
        handleError({ statusText: errorMessage });
        throw new Error(errorMessage);
      }
    }

    setUploadState("UploadStarted");
    formData.append(formDataFileKeyName, fileToUpload);

    if (typeof queryParams === "function") {
      queryParamsList = queryParams(fileMetaData) ?? [];
    } else {
      queryParamsList = queryParams ?? [];
    }

    if (queryParamsList) {
      queryParamsList.forEach((param) => {
        formData.append(param.name, param.value);
      });
    }

    let newxhr = new XMLHttpRequest();
    newxhr.responseType = "json";
    newxhr.onreadystatechange = () => {
      if (newxhr.readyState === 4) {
        var elem = document.getElementById("input");
        elem.parentNode.removeChild(elem);
        setUploadState("UploadFinished");
        if (newxhr.status === 201) {
          handleSuccess(newxhr.response);
        } else {
          handleError(newxhr);
        }
      }
    };
    newxhr.open("POST", url);
    newxhr.addEventListener("load", onUploadDone);
    newxhr.upload.addEventListener(
      "progress",
      function (event) {
        if (event.lengthComputable) {
          percent = (100 * event.loaded) / event.total;
          setProgress(percent);
        }
      },
      true
    );

    if (token) newxhr.setRequestHeader("Authorization", "Bearer " + token);

    newxhr.send(formData);
    setXHR(newxhr);
  };

  const cancelUpload = () => {
    xhr.abort();
    handleClose();
    setUploadState("FileNotSelected");
  };

  const handleClickOpen = () => {
    setModalOpen(true);
    setUploadState("FileNotSelected");
  };
  const handleClose = () => {
    setModalOpen(false);
  };

  return (
    <section>
      <span onClick={handleClickOpen}>{Component}</span>
      <Modal
        aria-describedby="modal-modal-description"
        aria-labelledby="modal-modal-title"
        onClose={handleClose}
        open={modalOpen}
      >
        <Wrapper>
          {uploadState === "UploadFinished" && !hasError && (
            <>
              <div>
                <p className="finished-msg sentence-case font-ds-headline-md ">{successMessage}</p>
                <p className="finished-file-name text-lds-foreground-muted font-ds-body-regular-xs">
                  {selectedFileName}
                </p>
              </div>
              <Button className="finished-btn" onClick={handleClose} size="lg" variant="primary">
                {t("general.OK")}
              </Button>
            </>
          )}
          {uploadState === "UploadFinished" && hasError && (
            <>
              <div>
                <p className="finished-msg sentence-case font-ds-headline-md ">
                  {t("general.sorry")}! {errorMessage}.
                </p>
                <p className="finished-file-name text-lds-purpose-status-negative font-ds-body-regular-xs">
                  {selectedFileName}
                </p>
              </div>
              <Button className="finished-btn" onClick={handleClose} size="lg" variant="primary">
                {t("general.OK")}
              </Button>
            </>
          )}
          {uploadState !== "UploadFinished" && (
            <>
              <h3 className="sentence-case font-ds-headline-md ">{headerTitle}</h3>
              <p className="sentence-case text-lds-foreground-muted font-ds-body-regular-xs">{headerText}</p>
              {renderModalBody?.({ uploadState })}
              <Dropzone
                className="dropzone"
                {...{
                  cancelUpload,
                  getInputProps,
                  getRootProps,
                  isDragActive,
                  open,
                  progress,
                  selectedFileName,
                  upload: uploadXHR,
                  uploadState,
                }}
              />
            </>
          )}
          <Icon className="close" iconName="Close" onClick={handleClose} />
        </Wrapper>
      </Modal>
    </section>
  );
}

const Wrapper = styled("section")(() => ({
  ".close": {
    cursor: "pointer",
    display: "block",
    position: "absolute",
    right: "2rem",
    top: "2rem",
  },
  ".dropzone": {
    height: "24.8rem",
    marginTop: "3rem",
    width: "100%",
  },
  ".finished-file-name": {
    marginTop: "0.4rem",
  },
  ".finished-msg": {
    marginTop: "8.4rem",
  },
  alignItems: "center",
  backgroundColor: "var(--color-surface-bright)",
  borderRadius: "7px",
  boxShadow: "var(--box-shadow-elevation-400)",
  display: "flex",
  flexDirection: "column",
  height: 450,
  justifyContent: "space-between",
  left: "50%",
  outline: "none",
  p: {
    marginTop: "0.4rem",
  },
  padding: "3.2rem",
  position: "absolute",
  textAlign: "center",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
}));
